import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';

export interface SidebarNavItemProps {
  /** Navigation target */
  href: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Display label */
  label: string;
  /** Whether this item represents the current route */
  isActive: boolean;
  /** Optional click handler (in addition to navigation) */
  onClick?: (e: React.MouseEvent) => void;
  /** Additional class names */
  className?: string;
}

export const SidebarNavItem = forwardRef<HTMLAnchorElement, SidebarNavItemProps>(
  ({ href, icon: Icon, label, isActive, onClick, className }, ref) => {
    return (
      <Link
        ref={ref}
        to={href}
        onClick={onClick}
        aria-current={isActive ? 'page' : undefined}
        className={clsx(
          'flex items-center gap-3 px-3 py-2 rounded-[var(--volt-radius-md)] text-sm font-medium',
          'transition-all duration-[var(--volt-transition-fast)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--volt-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--volt-dark)]',
          isActive
            ? [
                'bg-[var(--volt-blue-muted)] text-[var(--volt-blue)]',
                'border-l-2 border-[var(--volt-blue)]',
              ]
            : [
                'text-[var(--volt-text-secondary)]',
                'hover:bg-[var(--volt-dark-elevated)] hover:text-[var(--volt-text-primary)]',
                'border-l-2 border-transparent',
              ],
          className,
        )}
      >
        <Icon
          className={clsx(
            'h-5 w-5 flex-shrink-0',
            isActive ? 'text-[var(--volt-blue)]' : 'text-[var(--volt-text-muted)]',
          )}
          aria-hidden="true"
        />
        <span>{label}</span>
      </Link>
    );
  },
);

SidebarNavItem.displayName = 'SidebarNavItem';
